import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { X, BedDouble, Euro, SeparatorHorizontal } from "lucide-react"; // Добавил иконку разделителя, хотя не используется напрямую, но для понимания

export interface FilterState {
  country: string;
  minRooms: number;
  maxRooms: number;
  minPrice: number;
  maxPrice: number;
  propertyType: string;
  sortBy: string;
}

interface FiltersBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  properties: any[];
}

export default function FiltersBar({
  filters,
  onFiltersChange,
  properties,
}: FiltersBarProps) {
  const [priceRange, setPriceRange] = useState([
    filters.minPrice / 1000000,
    filters.maxPrice / 1000000,
  ]);
  const [roomRange, setRoomRange] = useState([
    filters.minRooms,
    filters.maxRooms,
  ]);

  // Calculate dynamic ranges from properties
  const { minRooms, maxRooms, minPrice, maxPrice } = React.useMemo(() => {
    if (properties.length === 0) {
      return {
        minRooms: 10,
        maxRooms: 50,
        minPrice: 1000000,
        maxPrice: 20000000,
      };
    }

    const rooms = properties.map((p) => p.roomsTotal || 0);
    const prices = properties
      .map((p) => p.purchasePrice || 0)
      .filter((p) => p > 0);

    return {
      minRooms: Math.min(...rooms),
      maxRooms: Math.max(...rooms),
      minPrice: prices.length > 0 ? Math.min(...prices) : 1000000,
      maxPrice: prices.length > 0 ? Math.max(...prices) : 20000000,
    };
  }, [properties]);

  // Update ranges when properties change
  React.useEffect(() => {
    // Convert to millions for display
    const currentMinPriceM = filters.minPrice / 1000000;
    const currentMaxPriceM = filters.maxPrice / 1000000;
    const dynamicMaxPriceM = maxPrice / 1000000;
    const dynamicMinPriceM = minPrice / 1000000;

    // Reset price range only if the current one is outside the new dynamic range
    if (
      currentMinPriceM < dynamicMinPriceM ||
      currentMaxPriceM > dynamicMaxPriceM
    ) {
      setPriceRange([dynamicMinPriceM, dynamicMaxPriceM]);
    } else {
      setPriceRange([currentMinPriceM, currentMaxPriceM]);
    }

    // Reset room range only if the current one is outside the new dynamic range
    if (filters.minRooms < minRooms || filters.maxRooms > maxRooms) {
      setRoomRange([minRooms, maxRooms]);
    } else {
      setRoomRange([filters.minRooms, filters.maxRooms]);
    }
  }, [
    minPrice,
    maxPrice,
    minRooms,
    maxRooms,
    filters.minPrice,
    filters.maxPrice,
    filters.minRooms,
    filters.maxRooms,
  ]);

  const countries = ["All Countries", "Spain", "Portugal", "Italy"];
  const propertyTypes = [
    "All Types",
    "Boutique Hotel",
    "Villa Hotel",
    "Beachfront Hotel",
    "Historic Palace Hotel",
    "Agriturismo",
    "Trullo Resort",
  ];
  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "priceAsc", label: "Price: Low → High" },
    { value: "priceDesc", label: "Price: High → Low" },
    { value: "roomsAsc", label: "Rooms: Low → High" },
    { value: "roomsDesc", label: "Rooms: High → Low" },
  ];

  const handleCountryChange = (value: string) => {
    onFiltersChange({ ...filters, country: value });
  };

  const handlePropertyTypeChange = (value: string) => {
    onFiltersChange({ ...filters, propertyType: value });
  };

  const handleSortChange = (value: string) => {
    onFiltersChange({ ...filters, sortBy: value });
  };

  const handlePriceRangeChange = (value: number[]) => {
    setPriceRange(value);
    onFiltersChange({
      ...filters,
      minPrice: Math.round(value[0] * 1000000), // Округление перед сохранением
      maxPrice: Math.round(value[1] * 1000000), // Округление перед сохранением
    });
  };

  const handleRoomRangeChange = (value: number[]) => {
    setRoomRange(value);
    onFiltersChange({
      ...filters,
      minRooms: Math.round(value[0]),
      maxRooms: Math.round(value[1]),
    });
  };

  const clearFilters = () => {
    const defaultFilters = {
      country: "All Countries",
      minRooms,
      maxRooms,
      minPrice,
      maxPrice,
      propertyType: "All Types",
      sortBy: "newest",
    };
    // Устанавливаем локальное состояние ползунков в их динамические пределы
    setPriceRange([minPrice / 1000000, maxPrice / 1000000]);
    setRoomRange([minRooms, maxRooms]);
    onFiltersChange(defaultFilters);
  };

  // Проверка активных фильтров стала более точной
  const hasActiveFilters = () => {
    const isDefaultPrice =
      filters.minPrice === minPrice && filters.maxPrice === maxPrice;
    const isDefaultRooms =
      filters.minRooms === minRooms && filters.maxRooms === maxRooms;

    return (
      filters.country !== "All Countries" ||
      filters.propertyType !== "All Types" ||
      !isDefaultRooms ||
      !isDefaultPrice ||
      filters.sortBy !== "newest"
    );
  };

  const formatPriceLabel = (priceM: number) => {
    if (priceM >= 1000) {
      return `${(priceM / 1000).toFixed(1)}B`;
    }
    return `${priceM.toFixed(1)}M`;
  };

  return (
    <div className="px-6 py-4 bg-white shadow-lg rounded-xl">
      {/* ======================================= 
        TOP ROW: Select Filters (Country, Type, Sort) 
      =========================================== */}
      {/* Сетка теперь 3 колонки на lg, чтобы они занимали всю ширину */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Country Filter */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            Country
          </label>
          <Select value={filters.country} onValueChange={handleCountryChange}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Property Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-800">
            Property Type
          </label>
          <Select
            value={filters.propertyType}
            onValueChange={handlePropertyTypeChange}
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select Type" />
            </SelectTrigger>
            <SelectContent>
              {propertyTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sort By Filter */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-800">
            Sort By
          </label>
          <Select value={filters.sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Sort Order" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <hr className="my-6 border-slate-200" />

      <div className="flex">
        <div className="grid md:grid-cols-2 gap-6 lg:w-3/4 xl:w-2/3">
          {/* Rooms Slider - UPDATED with Card and centering */}
          <Card className="shadow-md border-slate-200">
            <CardContent className="p-4 space-y-3">
              <label className="text-sm font-semibold text-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <BedDouble className="h-4 w-4 text-brand-600" />
                  Rooms Range
                </div>
                <span className="font-bold text-lg text-brand-700">
                  {Math.round(roomRange[0])} - {Math.round(roomRange[1])}
                </span>
              </label>
              <div className="flex justify-center pt-2">
                <Slider
                  value={roomRange}
                  onValueChange={handleRoomRangeChange}
                  max={maxRooms}
                  min={minRooms}
                  step={1}
                  className="w-[95%]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Price Slider - UPDATED with Card and centering */}
          <Card className="shadow-md border-slate-200">
            <CardContent className="p-4 space-y-3">
              <label className="text-sm font-semibold text-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Euro className="h-4 w-4 text-brand-600" />
                  Price Range (€)
                </div>
                <span className="font-bold text-lg text-brand-700">
                  {`€${formatPriceLabel(priceRange[0])} - €${formatPriceLabel(priceRange[1])}`}
                </span>
              </label>
              <div className="flex justify-center pt-2">
                <Slider
                  value={priceRange}
                  onValueChange={handlePriceRangeChange}
                  max={maxPrice / 1000000}
                  min={minPrice / 1000000}
                  step={0.5}
                  className="w-[95%]"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Visual Separator for Active Filters */}
      {hasActiveFilters() && <hr className="mt-8 mb-4 border-slate-200" />}

      {/* Active Filters Section */}
      {hasActiveFilters() && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3 flex-wrap">
            <span className="text-sm font-medium text-slate-700">
              Active filters:
            </span>
            <div className="flex items-center space-x-2 flex-wrap">
              {/* Country Badge */}
              {filters.country !== "All Countries" && (
                <Badge
                  variant="secondary"
                  className="flex items-center space-x-1 cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() => handleCountryChange("All Countries")}
                >
                  <span>{filters.country}</span>
                  <X className="h-3 w-3 ml-1 opacity-70" />
                </Badge>
              )}

              {/* Property Type Badge */}
              {filters.propertyType !== "All Types" && (
                <Badge
                  variant="secondary"
                  className="flex items-center space-x-1 cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() => handlePropertyTypeChange("All Types")}
                >
                  <span>{filters.propertyType}</span>
                  <X className="h-3 w-3 ml-1 opacity-70" />
                </Badge>
              )}

              {/* Price Range Badge (if not default) */}
              {!(
                filters.minPrice === minPrice && filters.maxPrice === maxPrice
              ) && (
                <Badge
                  variant="secondary"
                  className="flex items-center space-x-1 cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() =>
                    handlePriceRangeChange([
                      minPrice / 1000000,
                      maxPrice / 1000000,
                    ])
                  }
                >
                  <span>
                    Price: €{formatPriceLabel(filters.minPrice / 1000000)} - €
                    {formatPriceLabel(filters.maxPrice / 1000000)}
                  </span>
                  <X className="h-3 w-3 ml-1 opacity-70" />
                </Badge>
              )}

              {/* Rooms Range Badge (if not default) */}
              {!(
                filters.minRooms === minRooms && filters.maxRooms === maxRooms
              ) && (
                <Badge
                  variant="secondary"
                  className="flex items-center space-x-1 cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() => handleRoomRangeChange([minRooms, maxRooms])}
                >
                  <span>
                    Rooms: {Math.round(filters.minRooms)} -{" "}
                    {Math.round(filters.maxRooms)}
                  </span>
                  <X className="h-3 w-3 ml-1 opacity-70" />
                </Badge>
              )}

              {/* Sort By Badge */}
              {filters.sortBy !== "newest" && (
                <Badge
                  variant="secondary"
                  className="flex items-center space-x-1 cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() => handleSortChange("newest")}
                >
                  <span>
                    Sort:{" "}
                    {
                      sortOptions.find((opt) => opt.value === filters.sortBy)
                        ?.label
                    }
                  </span>
                  <X className="h-3 w-3 ml-1 opacity-70" />
                </Badge>
              )}
            </div>
          </div>

          {/* Clear All Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="flex items-center text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <X className="h-4 w-4 mr-1" />
            <span>Clear all filters</span>
          </Button>
        </div>
      )}
    </div>
  );
}
